import { useEffect } from 'react';
import { goToSsoLogin } from '@orgazone/ui-web';
import { BrowserRouter, Link, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ThemeVars } from '@orgazone/ui-web';
import { AppProvider, useApp } from './state';
import { Shell } from './components/Shell';
import { ToastHost, PageHead, Card } from './components/ui';
import { Login } from './screens/Login';
import { Reset } from './screens/Reset';
import { Supervision } from './screens/Supervision';
import { Journal } from './screens/Journal';
import { Fournisseurs } from './screens/Fournisseurs';
import { Clients } from './screens/Clients';
import { Mappings } from './screens/Mappings';
import { Transports } from './screens/Transports';
import { Certificats } from './screens/Certificats';
import { Utilisateurs } from './screens/Utilisateurs';
import { Maintenance } from './screens/Maintenance';
import { Parametres } from './screens/Parametres';

// Assemble le routeur : /login hors coquille (break-glass), le reste sous la coquille (rail + topbar).

/**
 * Envoie l'utilisateur se connecter, en SSO quand l'IdP est configuré.
 *
 * ⚠️ NAVIGATION PLEINE PAGE, pas une route du SPA : `/v1/auth/oidc/login` est une route SERVEUR (elle
 * pose le cookie d'état PKCE avant de partir chez l'IdP).
 *
 * ⚠️ Ce détour par l'écran local était le comportement d'AVANT (2026-08-11) : arriver sur l'EDI avec une
 * session Orgazone déjà ouverte affichait un formulaire au lieu de connecter. La Console et le store
 * partaient directement en SSO — trois surfaces du même IdP, deux comportements.
 */
function SeConnecter({ oidc }: { oidc: boolean }) {
  useEffect(() => { if (oidc) goToSsoLogin(); }, [oidc]); // séquence partagée (@orgazone/ui-web)
  if (!oidc) return <Navigate to="/login" replace />;
  return null;
}

// Porte de la coquille : tant que la session n'est pas résolue → rien (évite un flash) ; sans session
// ouverte → connexion ; connecté → la console.
function ShellLayout() {
  const { authReady, sessionUser, authMode } = useApp();
  if (!authReady) return null;
  // ⚠️ Plus de « mode ouvert » : l'API admin exige une session sans exception (audit 2026-08 §4), donc
  // l'écran l'exige aussi. Laisser entrer sans session n'afficherait que des 401.
  if (!sessionUser) return <SeConnecter oidc={authMode.oidc} />;
  return <Shell><Outlet /></Shell>;
}

function NotFound() {
  return (
    <>
      <PageHead title="Introuvable" sub="Cette page n'existe pas." />
      {/* ⚠️ « revenez à la supervision » ÉTAIT DU TEXTE MORT : la phrase désignait un geste qu'aucun lien
          n'offrait. Une consigne qui nomme une sortie doit y mener — sinon elle fait chercher ce qui
          n'existe pas (§23.0, même famille que le renvoi vers un onglet vide des Référentiels). */}
      <Card style={{ padding: 40, textAlign: 'center', color: 'var(--ink-500)', fontSize: 'var(--t-lead)' }}>
        Vérifiez l'adresse ou <Link to="/" style={{ color: 'var(--teal-700)', fontWeight: 600 }}>revenez à la supervision</Link>.
      </Card>
    </>
  );
}

export function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <ThemeVars defaultMode="light" />
        <ToastHost />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset" element={<Reset />} />
          <Route element={<ShellLayout />}>
            <Route path="/" element={<Supervision />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/fournisseurs" element={<Fournisseurs />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/mappings" element={<Mappings />} />
            <Route path="/transports" element={<Transports />} />
            <Route path="/certificats" element={<Certificats />} />
            <Route path="/utilisateurs" element={<Utilisateurs />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="/parametres" element={<Parametres />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}
